import React, { useEffect, useRef, useState } from 'react';
import { usePermissions } from '@michelin/central-provider';
import { useProductTour } from 'contexts/ProductTourContext';
import { useUIVersion } from 'contexts/UIVersionContext';
import GLOBAL_CONFIG from 'global-variables';
import { useGetWalkthroughStatus, useUpdateWalkthroughStatus } from 'hooks/useWalkthroughStatus';
import { useHistory } from 'react-router-dom';
import Tour from 'reactour';
import type { ReactourStep, ReactourStepContentArgs } from 'reactour';
import { Card } from './subComponents/Card';
import { Footer } from './subComponents/Footer';
import { Header } from './subComponents/Header';
import { StepBody } from './subComponents/StepBody';
import { useTranslatedSteps } from './subComponents/Steps';

export function ProductTour() {
  const { isNewUI } = useUIVersion();
  const history = useHistory();
  const permissions = usePermissions();
  const { setTourActive, registerStartTour } = useProductTour();
  const [isTourOpen, setIsTourOpen] = useState(false);
  const translatedSteps = useTranslatedSteps();
  const { data: walkthroughStatus } = useGetWalkthroughStatus();
  const { mutate: updateWalkthroughStatus } = useUpdateWalkthroughStatus();
  const hasAutoStartedRef = useRef(false);

  const accountNumber = permissions.location?.customer_number;
  const currentWalkthroughVersion = GLOBAL_CONFIG.currentWalkthroughVersion;

  const goToRouteSegment = (routeSegment?: string) => {
    if (routeSegment && accountNumber) {
      history.push(`/${accountNumber}/${routeSegment}`);
    }
  };

  const endTour = () => {
    setIsTourOpen(false);
    setTourActive(false);
    goToRouteSegment('home');
  };

  const startTour = () => {
    setTourActive(true);
    // reactour only calls `getCurrentStep` on step changes, not on the
    // initial open, so the first step's page navigation is triggered here.
    goToRouteSegment(translatedSteps[0].routeSegment);
    setIsTourOpen(true);
    updateWalkthroughStatus();
  };

  useEffect(() => {
    registerStartTour(startTour);
  }, [accountNumber]);

  useEffect(() => {
    if (!isNewUI || hasAutoStartedRef.current || walkthroughStatus == null) return;
    if (walkthroughStatus !== currentWalkthroughVersion) {
      hasAutoStartedRef.current = true;
      startTour();
    }
  }, [isNewUI, walkthroughStatus]);

  const steps: ReactourStep[] = translatedSteps.map((config) => ({
    selector: config.target,
    position: config.position,
    content: ({ step, goTo }: ReactourStepContentArgs) => {
      const index = step - 1;
      const isLast = index === translatedSteps.length - 1;

      return (
        <div>
          <Header index={index} total={translatedSteps.length} onClose={endTour} />
          <StepBody {...config} />
          <Footer
            index={index}
            total={translatedSteps.length}
            isLast={isLast}
            onBack={index > 0 ? () => goTo(index - 1) : undefined}
            onNext={() => (isLast ? endTour() : goTo(index + 1))}
          />
        </div>
      );
    },
  }));

  const handleStepChange = (index: number) => {
    goToRouteSegment(translatedSteps[index]?.routeSegment);
  };

  if (!isNewUI) return null;

  return (
    <Tour
      steps={steps}
      isOpen={isTourOpen}
      onRequestClose={endTour}
      getCurrentStep={handleStepChange}
      CustomHelper={Card}
      showButtons={false}
      showNavigation={false}
      showNumber={false}
      showCloseButton={false}
      rounded={12}
      accentColor="#0b2e59"
    />
  );
}

export default ProductTour;
